/**
 * Panel — framework-level overlay that renders content on top of the scene
 * with proper click-blocking, depth management, and lifecycle cleanup.
 *
 * Usage:
 *   const panel = new Panel(scene, { x: 400, y: 300, width: 300, height: 200 });
 *   panel.addText(0, -30, "Victory!", { fontSize: "22px", color: "#fbbf24" });
 *   panel.addButton(0, 40, "CONTINUE", 0x166534, 0x22c55e, () => doStuff());
 *   panel.show();          // fades in with click blocker
 *   panel.destroy();       // full cleanup
 */

declare const Phaser: any;

import { Depth } from "./Depth";

export interface PanelConfig {
  /** Center X of the panel content area */
  x: number;
  /** Center Y of the panel content area */
  y: number;
  /** Width of the content area (used for background if drawn) */
  width: number;
  /** Height of the content area (used for background if drawn) */
  height: number;
  /** Block all input behind the panel (default true) */
  blockInput?: boolean;
  /** Dim the full screen behind the panel (default false) */
  dim?: boolean;
  /** Dim overlay alpha (default 0.4) */
  dimAlpha?: number;
  /** Dim overlay color (default 0x000000) */
  dimColor?: number;
  /** Fade-in duration in ms (default 300, 0 = instant) */
  fadeDuration?: number;
  /** Fade-in delay in ms (default 0) */
  fadeDelay?: number;
}

interface PanelButton {
  bg: any;
  label: any;
}

export class Panel {
  private scene: any;
  private config: Required<PanelConfig>;
  private blocker: any | null = null;
  private elements: any[] = [];
  private buttons: PanelButton[] = [];
  private destroyed = false;

  constructor(scene: any, config: PanelConfig) {
    this.scene = scene;
    this.config = {
      x: config.x,
      y: config.y,
      width: config.width,
      height: config.height,
      blockInput: config.blockInput ?? true,
      dim: config.dim ?? false,
      dimAlpha: config.dimAlpha ?? 0.4,
      dimColor: config.dimColor ?? 0x000000,
      fadeDuration: config.fadeDuration ?? 300,
      fadeDelay: config.fadeDelay ?? 0,
    };
  }

  /**
   * Add a text element positioned relative to the panel center.
   * Returns the Phaser text object.
   */
  addText(
    offsetX: number,
    offsetY: number,
    content: string,
    style?: any
  ): any {
    const text = this.scene.add.text(
      this.config.x + offsetX,
      this.config.y + offsetY,
      content,
      {
        fontFamily: "system-ui, sans-serif",
        ...style,
      }
    );
    text.setOrigin(0.5);
    text.setDepth(Depth.OVERLAY_CONTENT);
    text.setAlpha(0);
    this.elements.push(text);
    return text;
  }

  /**
   * Add a clickable button positioned relative to the panel center.
   * Returns the button background rect.
   */
  addButton(
    offsetX: number,
    offsetY: number,
    label: string,
    fillColor: number,
    strokeColor: number,
    onClick: () => void,
    width = 140,
    height = 34
  ): any {
    const bx = this.config.x + offsetX;
    const by = this.config.y + offsetY;

    const bg = this.scene.add.rectangle(bx, by, width, height, fillColor, 0.9);
    bg.setStrokeStyle(2, strokeColor);
    bg.setDepth(Depth.OVERLAY_CONTENT);
    bg.setAlpha(0);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", onClick);

    const text = this.scene.add.text(bx, by, label, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "13px",
      color: "#ffffff",
      fontStyle: "bold",
    });
    text.setOrigin(0.5);
    text.setDepth(Depth.OVERLAY_CONTENT);
    text.setAlpha(0);

    this.elements.push(bg, text);
    this.buttons.push({ bg, label: text });
    return bg;
  }

  /**
   * Add any raw Phaser game object to the panel.
   * Sets depth and initial alpha automatically.
   */
  addRaw(obj: any): any {
    obj.setDepth(Depth.OVERLAY_CONTENT);
    obj.setAlpha(0);
    this.elements.push(obj);
    return obj;
  }

  /**
   * Show the panel — creates the input blocker and fades in all elements.
   */
  show(): void {
    if (this.destroyed) return;

    // Fullscreen input blocker
    if (this.config.blockInput) {
      const cam = this.scene.cameras.main;
      const bx = cam.centerX;
      const by = cam.centerY;
      const bw = cam.width;
      const bh = cam.height;

      if (this.config.dim) {
        this.blocker = this.scene.add.rectangle(
          bx, by, bw, bh,
          this.config.dimColor, this.config.dimAlpha
        );
      } else {
        // Invisible blocker — still absorbs clicks
        this.blocker = this.scene.add.rectangle(bx, by, bw, bh, 0x000000, 0);
      }
      this.blocker.setDepth(Depth.OVERLAY);
      this.blocker.setInteractive();
    }

    // Fade in elements
    const duration = this.config.fadeDuration;
    const delay = this.config.fadeDelay;

    if (duration > 0) {
      const doFade = () => {
        for (const el of this.elements) {
          this.scene.tweens.add({
            targets: el,
            alpha: 1,
            duration,
            ease: "Power2",
          });
        }
      };

      if (delay > 0) {
        this.scene.time.delayedCall(delay, doFade);
      } else {
        doFade();
      }
    } else {
      for (const el of this.elements) {
        el.setAlpha(1);
      }
    }
  }

  /**
   * Destroy the panel — removes all elements, blocker, and listeners.
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    for (const btn of this.buttons) {
      btn.bg.removeInteractive();
      btn.bg.removeAllListeners();
    }

    if (this.blocker) {
      this.blocker.removeInteractive();
      this.blocker.destroy();
      this.blocker = null;
    }

    for (const el of this.elements) {
      el.destroy();
    }

    this.elements = [];
    this.buttons = [];
  }
}
